import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ContactGroupService } from 'src/app/private/services/contact-group.service';
import { ContactService } from 'src/app/private/services/contact.service';
import { SharedModule } from 'src/app/private/shared/shared.module';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';

@Component({
  selector: 'app-add-contact-in-group',
  imports: [SharedModule],
  templateUrl: './add-contact-in-group.component.html',
  styleUrl: './add-contact-in-group.component.scss'
})
export class AddContactInGroupComponent {

  @Output() closeDialog = new EventEmitter();
  @Input() selectedGroupId: any;

  @Output() reload = new EventEmitter();
  @Input() preSelectedLeadIds: any[] = [];
  @Input() preSelectedContactIds: any[] = [];

  activeTabIndex: number = 0; // 0 for leads, 1 for contacts

  totalRecords: number = 0;
  rows: number = 50; // Default page size
  pageSizeOptions = [5, 10, 20, 50];
  loading: boolean = false;
  initialLoad: boolean = true;

  dataSource: any[] = [];
  btnLoading: boolean = false;

  selectedProducts: any[] = [];
  selectedLeads: any[] = [];
  selectedContacts: any[] = [];
  displayedColumns: any = [
    { field: 'contact_name', header: 'Contact Name', type: 'contact_name' },
    { field: 'email', header: 'Email', type: 'email' },
    { field: 'phone', header: 'Phone', type: 'phone' },
    // { field: 'source', header: 'Source', type: 'source' }
  ];

  contactsDisplayedColumns: any = [
    { field: 'contact_name', header: 'Contact Name', type: 'contact_name' },
    { field: 'email', header: 'Email', type: 'email' },
    { field: 'phone', header: 'Phone', type: 'phone' }
  ];

  constructor(
    private localStorage: LocalStorageService,
    private messageService: MessageService,
    private api: ContactGroupService,
    private contactService: ContactService
  ) { }

  ngOnInit() {
    // Load initial data when dialog opens
    // this.loadData({ first: 0, rows: this.rows });
    this.selectedProducts = this.activeTabIndex === 0 ? this.selectedLeads : this.selectedContacts;
  }

  handleCloseDialog() {
    this.closeDialog.emit();
  }

  loadData(event: any) {

    if(this.initialLoad){
      this.initialLoad = false;
      return;
    }

    if (this.activeTabIndex === 0) {
      this.loadLeadsData(event);
    } else {
      this.loadContactsData(event);
    }
  }

  loadLeadsData(event: any) {
    const page = event.first || 0;
    const size = event.rows || this.rows;

    const param = {
      "where": {
        "organization_id": this.localStorage.getOrganizationId(),
        "is_actual_lead" : true,
        // "current_status": "lead"
      },
      "filter": null,
      "page": page,
      "limit": size,
      "order": {
        "col": "\"lead\".\"updatedAt\"",
        "dir": "DESC"
      }
    }

    this.loading = true;

    this.api.getAllLeads(param).subscribe({
      next: (data) => {
        this.dataSource = data.rows || [];
        this.totalRecords = data.count || 0;
        this.loading = false;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load leads.' });
        this.loading = false;
      },
    });
  }

  loadContactsData(event: any) {
    const page = event.first || 0;
    const size = event.rows || this.rows;

    const param = {
      "where": {
        "organization_id": this.localStorage.getOrganizationId()
      },
      "filter": null,
      "page": page,
      "limit": size,
      "order": {
        "col": "updatedAt",
        "dir": "DESC"
      }
    }

    this.loading = true;

    this.contactService.getAllContactsWithCount(param).subscribe({
      next: (data) => {
        this.dataSource = data.rows || [];
        this.totalRecords = data.count || 0;
        this.loading = false;
      },
      error: () => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load contacts.' });
        this.loading = false;
      },
    });
  }

  onTabChange(newValue: number | string) {
    const tabIndex = typeof newValue === 'string' ? parseInt(newValue) : newValue;
    console.log('Tab changed to index:', tabIndex);
    
    // Save current selections before switching
    if (this.activeTabIndex === 0) {
      this.selectedLeads = [...this.selectedProducts];
    } else {
      this.selectedContacts = [...this.selectedProducts];
    }
    
    // Load selections for the new tab
    if (tabIndex === 0) {
      this.selectedProducts = [...this.selectedLeads];
    } else {
      this.selectedProducts = [...this.selectedContacts];
    }
    
    this.dataSource = [];
    this.totalRecords = 0;
    // Trigger data load for the new tab
    this.loadData({ first: 0, rows: this.rows });
  }

  getFullName(user: any): string {
    return [user?.prefix, user?.first_name, user?.last_name]
      .filter(Boolean)
      .join(' ') || '';
  }

  isLeadDisabled(lead: any): boolean {
    return this.preSelectedLeadIds.includes(lead.lead_id);
  }

  isContactDisabled(contact: any): boolean {
    return this.preSelectedContactIds.includes(contact.contact_id);
  }

  isRowDisabled(rowData: any): boolean {
    if (this.activeTabIndex === 0) {
      return this.isLeadDisabled(rowData);
    } else {
      return this.isContactDisabled(rowData);
    }
  }


  onSubmit() {

    // Save current tab selections before submitting
    if (this.activeTabIndex === 0) {
      this.selectedLeads = [...this.selectedProducts];
    } else {
      this.selectedContacts = [...this.selectedProducts];
    }

    // Combine all selections from both tabs
    const allSelections = [...this.selectedLeads, ...this.selectedContacts];

    console.log('All selected contacts/leads:', allSelections);

    if (allSelections.length === 0) {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please select at least one contact' });
      return;
    }

    const updatedContacts = allSelections
      .filter((contact) => {
        // Filter out preselected leads and contacts
        if (contact.lead_id) {
          return !this.preSelectedLeadIds.includes(contact.lead_id);
        } else if (contact.contact_id) {
          return !this.preSelectedContactIds.includes(contact.contact_id);
        }
        return true;
      })
      .map((contact) => {
      return {
        prefix : contact.prefix,
        first_name: contact.first_name,
        last_name: contact.last_name,
        name: this.getFullName(contact),
        email: contact.email,
        phone: contact.phone,
        user_id: contact.user_id,
        lead_id: contact.lead_id || null,
        contact_id: contact.contact_id || null,
        campus_id: contact.campus_id,
        organization_id: contact.organization_id,
        source: contact.lead_id ? contact.source : 'Contact',
      }
    }
    );

    console.log('Contacts after excluding preselected:', updatedContacts);

    // remove duplicates based on email and phone

    let uniqueContacts : any[] = updatedContacts;

    // updatedContacts.forEach((contact) => {
    //   const found = uniqueContacts.some(el => el.email === contact.email || el.phone === contact.phone);
    //   if (!found) uniqueContacts.push(contact);
    // });

    if(uniqueContacts.length === 0){
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please select at least one with unique email or phone' });
      return;
    }

    // unique contacts

    this.btnLoading = true;
    const payload = {
      organization_id: this.localStorage.getOrganizationId(),
      contact_group_id: this.selectedGroupId,
      contacts: uniqueContacts,
      user_id: this.localStorage.getUserId()
    }


    this.api.createContactGroupList(payload).subscribe(
      (response) => {
        this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Contact added in group successfully' });
        this.btnLoading = false;
        this.closeDialog.emit();
        this.reload.emit();
      },
      (error) => {
        console.log(error);
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to add contact in group' });
        this.btnLoading = false;
      }
    );
  }

}
